import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SivaGoalsStatus, SivaManagementService } from 'src/api/v2';
import { SIVA_GOALS_DETAILS_KEY, SIVA_GOALS_DETAILS_LIST_KEY } from 'src/constants/hook-keys';
import { useAuth } from 'src/features/authorization';
import { useCluster } from 'src/features/cluster';

export function useSivaGoalsDetails(clusterId: string | undefined, year: number | undefined) {
  const { cluster } = useCluster();
  const { checkAccess } = useAuth();
  const { read } = checkAccess('SivaAdmin');

  const [service] = useState(() => new SivaManagementService());
  const queryClient = useQueryClient();

  const { data, ...rest } = useQuery({
    queryKey: [SIVA_GOALS_DETAILS_KEY, clusterId, year],
    queryFn: () => service.getSivaGoalsDetails(year!, clusterId!),
    enabled: !!cluster && !!clusterId && !!year && !isNaN(year) && read,
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!data?.id) {
        throw new Error('Missing id from SivaGoalsDetailsDto');
      }

      return service.reviewSivaClusterDetails(data?.id, SivaGoalsStatus.Approved);
    },
    onSettled() {
      queryClient.resetQueries([SIVA_GOALS_DETAILS_KEY, clusterId, year]);
      queryClient.resetQueries([SIVA_GOALS_DETAILS_LIST_KEY, year]);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!data?.id) {
        throw new Error('Missing id from SivaGoalsDetailsDto');
      }

      return service.reviewSivaClusterDetails(data?.id, SivaGoalsStatus.ChangeRequested);
    },
    onSettled() {
      queryClient.resetQueries([SIVA_GOALS_DETAILS_KEY, clusterId, year]);
      queryClient.resetQueries([SIVA_GOALS_DETAILS_LIST_KEY, year]);
    },
  });

  return {
    data,

    ...rest,

    mutations: {
      approve: approveMutation,
      reject: rejectMutation,
    },
  };
}
